import * as Styled from "./EventFeedbackResultsModal.styles";
import {
    Box,
    CircularProgress,
    Divider,
    Typography,
    Checkbox,
    IconButton,
    FormControlLabel,
    useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadOffIcon from "@mui/icons-material/FileDownloadOff";
import SearchIcon from "@mui/icons-material/Search";
import BaseModal from "../BaseModal/BaseModal";
import { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { getEventFeedbackMetrics } from "../../store/actions/feedbackActions";

type EventFeedbackResultsModalProps = {
    open: boolean;
    onClose: () => void;
    eventId: string;
    eventName: string;
    eventOrganizerEmail: string;
};

const QUESTIONS = [
    {
        id: "satisfaction",
        label: "Rate your overall satisfaction with the event by using the slider below.",
        required: true,
    },
    {
        id: "reason",
        label: "Why did you rate this way? Please provide context or suggestions below.",
        required: false,
    },
];

const EventFeedbackResultsModal = ({ open, onClose, eventId, eventName, eventOrganizerEmail }: EventFeedbackResultsModalProps) => {
    const theme = useTheme();
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
    const [filterProvidedFeedbackOnly, setFilterProvidedFeedbackOnly] = useState(false);
    const [downloadingCSV, setDownloadingCSV] = useState(false);
    const [downloadCSVFailed, setDownloadCSVFailed] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const userEmail = useSelector((state: RootState) => state.user.email);
    const feedbacks = useSelector((state: RootState) => state.eventFeedbacks.data?.attended || []);
    const metrics = useSelector((state: RootState) => state.eventFeedbacks.data);
    const loading = useSelector((state: RootState) => state.eventFeedbacks.loading);
    const isCurrentUserOrganizer = eventOrganizerEmail === userEmail;

    useEffect(() => {
        if (feedbacks.length > 0 && !selectedEmail) {
            setSelectedEmail(feedbacks[0].participantEmail);
        }
    }, [feedbacks, selectedEmail]);

    useEffect(() => {
        if (eventId) {
            dispatch(getEventFeedbackMetrics(eventId));
        }
    }, [dispatch, eventId]);

    if (!isCurrentUserOrganizer) return null;

    // -2 = declined, -1 = no-rating fallback; both mean "no real feedback provided".
    const hasProvidedFeedback = (rating: number | undefined) =>
        rating !== undefined && rating !== -2 && rating !== -1;

    const filteredFeedbacks = filterProvidedFeedbackOnly
        ? feedbacks.filter((f) => hasProvidedFeedback(f.eventFeedbackResponse?.rating))
        : feedbacks;

    const feedbackCount = metrics?.countSubmittedFeedback || 0;
    const declinedCount = metrics?.countDeclinedFeedback || 0;
    const averageRating = metrics?.averageEventRating || 0;
    const totalAttendees = metrics?.countAttended || 0;

    const convertToCSV = (data: any[]): string => {
        if (!data.length) return "";
        const headers = Object.keys(data[0]);
        const rows = data.map((row) =>
            headers.map((header) => {
                const cell = row[header] ?? "";
                return `"${cell.toString().replace(/"/g, '""')}"`;
            }).join(",")
        );
        return [headers.join(","), ...rows].join("\n");
    };

    const handleDownloadCSV = () => {
        try {
            setDownloadingCSV(true);
            const feedbackData = feedbacks.map((f) => {
                const feedback = f.eventFeedbackResponse;
                if (!feedback || feedback.rating === -1) {
                    return {
                        "Attendee Name": f.participantName,
                        Email: f.participantEmail,
                        "Event Rating": "N/A",
                        Comments: "N/A",
                    };
                }
                if (feedback.rating === -2) {
                    return {
                        "Attendee Name": f.participantName,
                        Email: f.participantEmail,
                        "Event Rating": "Declined to answer",
                        Comments: "",
                    };
                }
                return {
                    "Attendee Name": f.participantName,
                    Email: f.participantEmail,
                    "Event Rating": `${feedback.rating}/10`,
                    Comments: feedback.additionalComments?.trim() || "N/A",
                };
            });

            const csvString = convertToCSV(feedbackData);
            const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", `EventFeedback_${eventName}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setDownloadCSVFailed(true);
        } finally {
            setDownloadingCSV(false);
        }
    };

    const handleSelect = (email: string) => {
        setSelectedEmail((prev) => (prev === email ? null : email));
    };

    if (loading) {
        return (
            <BaseModal open={open} onClose={onClose}>
                <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                    <CircularProgress />
                </Box>
            </BaseModal>
        );
    }

    return (
        <BaseModal open={open} onClose={onClose} fullScreenTakeover padding="0px">
            <>
                <Styled.ModalTitleContainer>
                    <Typography variant="h2" sx={{ marginBottom: theme.spacing(2) }}>
                        {`Event Attendees (${totalAttendees})`}
                    </Typography>
                    <Styled.SubtitleActionText
                        variant="modalSubtitle"
                        onClick={handleDownloadCSV}
                        style={{
                            color: downloadCSVFailed
                                ? theme.colors.action.failure
                                : theme.colors.primary.slalomDarkBlue,
                        }}
                    >
                        {downloadingCSV ? (
                            <CircularProgress
                                size={20}
                                color="secondary"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                        ) : downloadCSVFailed ? (
                            <FileDownloadOffIcon
                                color="error"
                                style={{
                                    width: 20,
                                    height: 20,
                                    marginRight: theme.spacing(1),
                                }}
                            />
                        ) : (
                            <DownloadIcon
                                color="secondary"
                                style={{
                                    width: 20,
                                    height: 20,
                                    marginRight: theme.spacing(1),
                                }}
                            />
                        )}
                        {downloadCSVFailed
                            ? "Failed to Download CSV"
                            : "Download Feedback as CSV"}
                    </Styled.SubtitleActionText>
                </Styled.ModalTitleContainer>

                <Styled.StatsRow sx={{ justifyContent: "flex-start", alignItems: "flex-end" }}>
                    <Styled.StatBox>
                        <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
                            <Box component="span" sx={{ color: theme.colors.primary.slalomBlue, fontWeight: 600 }}>
                                {feedbackCount}
                            </Box>
                            {" / "}
                            <Box component="span" sx={{ color: theme.colors.neutral.black, fontWeight: 600 }}>
                                {feedbacks.length}
                            </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 400, color: theme.colors.neutral.black, marginTop: theme.spacing(1.2), }}>
                            Feedback responses
                        </Typography>
                    </Styled.StatBox>

                    <Box
                        sx={{
                            borderLeft: `1px solid ${theme.colors.neutral.softGray}`,
                            height: "100px",
                            alignSelf: "center",
                            mx: 0,
                        }}
                    />

                    <Styled.StatBox>
                        <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
                            <Box component="span" sx={{ color: theme.colors.primary.slalomBlue, fontWeight: 600 }}>
                                {declinedCount}
                            </Box>
                            {" / "}
                            <Box component="span" sx={{ color: theme.colors.neutral.black, fontWeight: 600 }}>
                                {feedbacks.length}
                            </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 400, color: theme.colors.neutral.black, marginTop: theme.spacing(1.2), }}>
                            Declined feedback
                        </Typography>
                    </Styled.StatBox>

                    <Box
                        sx={{
                            borderLeft: `1px solid ${theme.colors.neutral.softGray}`,
                            height: "100px",
                            alignSelf: "center",
                            mx: 1,
                        }}
                    />

                    <Styled.StatBox>
                        <Typography
                            variant="caption"
                            sx={{
                                fontStyle: "italic",
                                fontSize: 14,
                                color: theme.colors.neutral.darkGray,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            Average Rating
                        </Typography>

                        <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
                            <Box component="span" sx={{ color: theme.colors.primary.slalomBlue, fontWeight: 600 }}>
                                {averageRating}
                            </Box>
                            {" / "}
                            <Box component="span" sx={{ color: theme.colors.neutral.black, fontWeight: 600 }}>
                                10
                            </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 400, color: theme.colors.neutral.black, marginTop: theme.spacing(1.2), }}>
                            Overall satisfaction
                        </Typography>
                    </Styled.StatBox>
                </Styled.StatsRow>

                <Styled.HorizontalStack direction="row">
                    <Styled.LeftColumn isSelected={!!selectedEmail}>
                        <Styled.InnerLeftColumn>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <Typography
                                    variant="modalSubtitle"
                                    sx={{ fontSize: 14, fontWeight: 600 }}
                                >
                                    Employee name
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={filterProvidedFeedbackOnly}
                                                onChange={(e) => setFilterProvidedFeedbackOnly(e.target.checked)}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: theme.spacing(1),
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                                Provided Feedback
                                            </Typography>
                                        }
                                        sx={{ margin: 0 }}
                                    />

                                    <IconButton size="small">
                                        <SearchIcon sx={{ fontSize: 28 }} />
                                    </IconButton>
                                </Box>
                            </Box>

                            {filteredFeedbacks.map((user, index) => (
                                <Styled.AttendeeBox
                                    key={user.participantEmail}
                                    isSelected={selectedEmail === user.participantEmail}
                                    isLast={index === filteredFeedbacks.length - 1}
                                    onClick={() => handleSelect(user.participantEmail)}
                                >
                                    <Typography variant="modalBody">{user.participantName}</Typography>
                                </Styled.AttendeeBox>
                            ))}
                        </Styled.InnerLeftColumn>
                    </Styled.LeftColumn>

                    <Styled.RightColumn isSelected={!!selectedEmail}>
                        <Styled.InnerRightColumn>
                            {selectedEmail && (
                                <>
                                    <Typography variant="h3" sx={{ mb: theme.spacing(3) }}>
                                        Feedback response
                                    </Typography>

                                    {(() => {
                                        const currentFeedback = feedbacks.find(
                                            (f) => f.participantEmail === selectedEmail
                                        );

                                        const feedback = currentFeedback?.eventFeedbackResponse;
                                        const declined = feedback?.rating === -2;
                                        const answers = [
                                            {
                                                questionId: "satisfaction",
                                                answer: hasProvidedFeedback(feedback?.rating) ? `${feedback!.rating}/10` : "",
                                            },
                                            {
                                                questionId: "reason",
                                                answer: feedback?.additionalComments || "",
                                            },
                                        ];

                                        return (
                                            <Styled.SurveyAnswerContainer>
                                                {QUESTIONS.map((question, i) => {
                                                    const matched = answers.find(
                                                        (a) => a.questionId === question.id
                                                    );

                                                    const displayAnswer =
                                                        matched?.answer ||
                                                        (declined
                                                            ? "Declined to provide feedback"
                                                            : "No Response");

                                                    return (
                                                        <Box key={question.id} sx={{ mb: i !== QUESTIONS.length - 1 ? theme.spacing(3) : 0 }}>
                                                            <Typography
                                                                variant="modalSubtitle"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "16px",
                                                                    mb: 2,
                                                                    "&::after": question.required
                                                                        ? { content: '" *"', color: theme.colors.neutral.black }
                                                                        : {}
                                                                }}
                                                            >
                                                                {question.label}
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontSize: "16px",
                                                                    color:
                                                                        displayAnswer === "Declined to provide feedback" ||
                                                                            displayAnswer === "No Response"
                                                                            ? theme.colors.neutral.darkGray
                                                                            : theme.colors.neutral.black,
                                                                    fontStyle:
                                                                        displayAnswer === "Declined to provide feedback" ||
                                                                            displayAnswer === "No Response"
                                                                            ? "italic"
                                                                            : "normal"
                                                                }}
                                                            >
                                                                {displayAnswer}
                                                            </Typography>

                                                            {i !== QUESTIONS.length - 1 && (
                                                                <Divider
                                                                    sx={{
                                                                        width: "100%",
                                                                        marginY: theme.spacing(3),
                                                                        backgroundColor: theme.colors.secondary.purple
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Styled.SurveyAnswerContainer>
                                        );
                                    })()}
                                </>
                            )}
                        </Styled.InnerRightColumn>
                    </Styled.RightColumn>
                </Styled.HorizontalStack>
            </>
        </BaseModal>
    );
};

export default EventFeedbackResultsModal;
